import { create } from "zustand";
import type { Client } from "../types/client";
import type { Vehicle } from "../types/vehicle";

const STORAGE_KEY = "garage-active-client";

type ActiveClientSnapshot = {
  client: Client | null;
  vehicle: Vehicle | null;
  technicalEvaluationId: string | null;
};

interface ActiveClientState extends ActiveClientSnapshot {
  isHydrated: boolean;
  initialize: () => void;
  setActiveClient: (client: Client) => void;
  setActiveVehicle: (vehicle: Vehicle | null) => void;
  clearActiveVehicle: () => void;
  setTechnicalEvaluationId: (id: string | null) => void;
  clearActiveClient: () => void;
}

const readPersistedClient = (): ActiveClientSnapshot => {
  if (typeof window === "undefined") {
    return { client: null, vehicle: null, technicalEvaluationId: null };
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { client: null, vehicle: null, technicalEvaluationId: null };
    const parsed = JSON.parse(raw) as ActiveClientSnapshot;
    return {
      client: parsed.client ?? null,
      vehicle: parsed.vehicle ?? null,
      technicalEvaluationId: parsed.technicalEvaluationId ?? null,
    };
  } catch {
    return { client: null, vehicle: null, technicalEvaluationId: null };
  }
};

const persistClient = (snapshot: ActiveClientSnapshot) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore persistence errors */
  }
};

export const useActiveClientStore = create<ActiveClientState>((set, get) => ({
  client: null,
  vehicle: null,
  technicalEvaluationId: null,
  isHydrated: false,
  initialize: () => {
    if (get().isHydrated) return;
    const snapshot = readPersistedClient();
    set({ ...snapshot, isHydrated: true });
  },
  setActiveClient: (client) => {
    const snapshot: ActiveClientSnapshot = {
      client,
      vehicle: null,
      technicalEvaluationId: null,
    };
    persistClient(snapshot);
    set({ ...snapshot, isHydrated: true });
  },
  setActiveVehicle: (vehicle) => {
    const currentVehicleId = get().vehicle?.id ?? null;
    const shouldKeepTechnicalEvaluationId =
      vehicle && currentVehicleId === vehicle.id;
    const snapshot: ActiveClientSnapshot = {
      client: get().client,
      vehicle,
      technicalEvaluationId: shouldKeepTechnicalEvaluationId
        ? get().technicalEvaluationId
        : null,
    };
    persistClient(snapshot);
    set({ ...snapshot, isHydrated: true });
  },
  clearActiveVehicle: () => {
    const snapshot: ActiveClientSnapshot = {
      client: get().client,
      vehicle: null,
      technicalEvaluationId: null,
    };
    persistClient(snapshot);
    set({ ...snapshot, isHydrated: true });
  },
  setTechnicalEvaluationId: (id) => {
    const snapshot: ActiveClientSnapshot = {
      client: get().client,
      vehicle: get().vehicle,
      technicalEvaluationId: id,
    };
    persistClient(snapshot);
    set({ ...snapshot, isHydrated: true });
  },
  clearActiveClient: () => {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
    set({ client: null, vehicle: null, technicalEvaluationId: null, isHydrated: true });
  },
}));
